import React, {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  VipWebHalfCard,
  VipWebWeeklyRewardModal,
} from '@fe-progressive/package-progressive/vip-web';
import {Button} from 'antd-mobile';
import {useFocusEffect} from '@react-navigation/native';
import {Trans, useTranslation} from 'react-i18next';
import {cn, goTo, toPriceStr} from '@/utils';
import globalStore from '@/services/global.state';
import {getCurrentVip} from '@/utils/vip';
import {vipOptionsV2, WeeklyCardType} from './vip.variable';
import {
  getVipConfigListV2,
  VipConfigVO,
  weekCardReceive,
  weekRewardReceive,
} from './vip.service';
import {canUseType} from './week-card';
import WeeklyCardReceiving, {
  WeeklyCardReceivingRef,
  WeeklyCardReward,
} from './modal/weekly-card-reward';

export interface VipInfo {
  level: number;
  currentValue?: number;
  nextValue?: number;
}

interface VipHalfCardProps {
  login: boolean;
  onVipUpdate?: (vip: VipInfo) => void;
  renderBottomAction?: (params: {
    vip: VipInfo;
    login: boolean;
    maxLevel: number;
    curTextClass: string;
  }) => React.ReactNode;
  renderRedPoint?: (vip: VipInfo) => React.ReactNode;
  className?: string;
}

export interface VipHalfCardProgressiveRef {
  init: () => void;
}

const VipHalfCardProgressive = forwardRef<
  VipHalfCardProgressiveRef,
  VipHalfCardProps
>(
  (
    {login, onVipUpdate, renderBottomAction, renderRedPoint, className},
    ref,
  ) => {
    const [maxLevel, setMaxLevel] = useState(vipOptionsV2.length - 1);
    const [lastVip, setLastVip] = useState<VipConfigVO | null>(null);
    const [vip, setVip] = useState<VipInfo>({
      level: 0,
      currentValue: 0,
      nextValue: 0,
    });
    const weeklyCardRef = useRef<WeeklyCardReceivingRef>(null);
    const [weeklyReward, setWeeklyReward] = useState<{
      baseAmount: number;
      bonusAmount: number;
      multiple: number;
    } | null>(null);
    const {i18n, t} = useTranslation();

    const init = useCallback(() => {
      getVipConfigListV2().then(res => {
        const list = res.sort((a, b) => (a.level || 0) - (b.level || 0));
        const baseExp = localStorage.getItem('vipBaseExp');
        if (baseExp) {
          localStorage.removeItem('vipBaseExp');
          let {currentVip, lastVip: prevVip} = getCurrentVip(
            list,
            true,
            +baseExp,
          );
          setVip({
            level: currentVip.level || 0,
            currentValue: +baseExp,
            nextValue: currentVip.nextLevelExperience || 0,
          });
          setLastVip(prevVip);
          setTimeout(() => {
            ({currentVip, lastVip: prevVip} = getCurrentVip(list));
            setVip({
              level: currentVip?.level || 0,
              currentValue: currentVip?.currentExperience || 0,
              nextValue: currentVip?.nextLevelExperience || 0,
            });
            setLastVip(prevVip);
          }, 200);
          return;
        }

        const {currentVip, lastVip: prevVip} = getCurrentVip(list);
        setMaxLevel(list?.[list.length - 1]?.level || 0);
        setVip({
          level: currentVip?.level || 0,
          currentValue: currentVip?.currentExperience || 0,
          nextValue: currentVip?.nextLevelExperience || 0,
        });
        setLastVip(prevVip);
      });
    }, []);

    useImperativeHandle(ref, () => ({init}), [init]);

    const handleShowWeeklyCard = useCallback(() => {
      weekCardReceive().then(res => {
        const prizeList = (res || [])
          .filter(
            v =>
              v.bonusAmount &&
              v.bonusAmount > 0 &&
              (v.scratchCount || v.scratchDay) &&
              (v.spinCount || v.spinDay),
          )
          .map(v => {
            const cardType = canUseType.find(type =>
              v.cardName?.toLowerCase().includes(type),
            ) as WeeklyCardType | undefined;
            if (!cardType) {
              return null;
            }
            return {
              cardType,
              money: v.bonusAmount || 0,
              scratchCard: v.scratchCount || 0,
              scratchCount: v.scratchDay || 0,
              spin: v.spinCount || 0,
              spinCount: v.spinDay || 0,
            };
          });
        const filteredPrizeList = prizeList.filter(
          v => !!v,
        ) as WeeklyCardReward[];
        if (filteredPrizeList.length > 0) {
          weeklyCardRef.current?.show(filteredPrizeList);
        }
      });
    }, []);

    const handleShowWeeklyReward = useCallback(() => {
      weekRewardReceive().then(res => {
        if (res && res.weekBaseBonusAmount && res.bonusAmount) {
          setWeeklyReward({
            baseAmount: res.weekBaseBonusAmount || 0,
            multiple: res.multiple || 0,
            bonusAmount: res.bonusAmount || 0,
          });
        }
      });
    }, []);

    useFocusEffect(
      useCallback(() => {
        if (!login) {
          return;
        }
        handleShowWeeklyCard();
        handleShowWeeklyReward();
      }, [handleShowWeeklyCard, handleShowWeeklyReward, login]),
    );

    useEffect(() => {
      onVipUpdate?.(vip);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [vip]);

    const options = useMemo(() => {
      if (!vipOptionsV2[vip.level]) {
        return vipOptionsV2[0];
      }
      return vipOptionsV2[vip.level];
    }, [vip.level]);

    return (
      <>
        <VipWebHalfCard
          vip={{
            ...vip,
            lastNextValue: lastVip?.nextLevelExperience || 0,
          }}
          maxLevel={maxLevel}
          isLoggedIn={login}
          assets={{
            background: options.background,
            sign: options.sign,
            progressLinear: options.progressLinear,
            titleClassName: options.titleClass,
            currentTextClassName: options.curTextClass,
          }}
          className={className}
          onClick={() => goTo('Vip')}
          onLoginRequired={() => goTo('Login')}
          onRecharge={() => goTo('Recharge')}
          renderRedPoint={renderRedPoint}
          renderBottomAction={({currentTextClassName}) =>
            renderBottomAction ? (
              renderBottomAction({
                vip,
                login,
                maxLevel,
                curTextClass: currentTextClassName || '',
              })
            ) : (
              <>
                <div
                  className={cn(
                    'text-[0.625rem] flex-1',
                    currentTextClassName,
                  )}>
                  {vip.level != null && vip.level < maxLevel ? (
                    <Trans
                      i18nKey="vip.rechargeTip"
                      components={[
                        <span className="font-bold text-[#F88B1D]" />,
                      ]}
                      values={{
                        price: toPriceStr(
                          vip.nextValue && vip.currentValue != null
                            ? Math.max(
                                Number(vip.nextValue) -
                                  Number(vip.currentValue),
                                0,
                              )
                            : 0,
                          {
                            fixed: 0,
                          },
                        ),
                        level: `VIP${(vip.level || 0) + 1}`,
                      }}
                    />
                  ) : (
                    t('vip.incoming')
                  )}
                </div>
                <Button
                  className="px-3 h-6 bg-c1 text-white text-[0.625rem] p-0 text-center justify-center items-center rounded-full before:rounded-full shadow-[0px_1px_4px_0px_rgba(0,0,0,0.25),0px_1px_0px_0px_rgba(255,255,255,0.20)_inset]"
                  onClick={event => {
                    event.stopPropagation();
                    if (!login) {
                      goTo('Login');
                      return;
                    }
                    goTo('Recharge');
                  }}>
                  {t('label.recharge')}
                </Button>
              </>
            )
          }
        />
        <WeeklyCardReceiving ref={weeklyCardRef} />
        <VipWebWeeklyRewardModal
          visible={weeklyReward != null}
          amount={weeklyReward?.bonusAmount}
          baseAmount={weeklyReward?.baseAmount}
          multiple={weeklyReward?.multiple}
          locale={i18n.language}
          currencySymbol={globalStore.currency}
          onClose={() => setWeeklyReward(null)}
        />
      </>
    );
  },
);

export default VipHalfCardProgressive;
